/**
 * Norlyth Custom Builder Engine (Advanced Layered Visualizer)
 */

const CATEGORY_ORDER = [
  'General Options',
  'Body Options',
  'Neck Options',
  'Electronics Options',
  'Hardware Options',
  'Export/Load'
];

const PROPERTY_ORDER = [
  'Model',
  'Dexterity',
  'Strings',
  'Scale Length',
  'Guitar Case',
  'Bevel',
  'Body Wood',
  'Body Finish',
  'Solid Colors',
  'Standard Pattern',
  'Custom Finish',
  'Top Coat',
  'Neck Wood',
  'Number of Frets',
  'Fingerboard Wood',
  'Inlay Shape',
  'Neck Rear Finish',
  'Headstock Shape',
  'Headstock Overlays',
  'Headstock Finish',
  'Pickup Model',
  'Pickup Color',
  'Pickup Configuration',
  'Bridge',
  'Hardware Color',
  'Strings Type',
  'Knobs',
  'Output Jack'
];

const MUTUALLY_EXCLUSIVE_RULES = {
  'Solid Colors': ['Custom Finish', 'Standard Pattern'],
  'Standard Pattern': ['Solid Colors', 'Custom Finish'],
  'Custom Finish': ['Solid Colors', 'Standard Pattern']
};

function isOptionCompatible(opt, modelHandle) {
  if (!modelHandle) return false;
  const isWhitelisted = !opt.compatibleModel || opt.compatibleModel.includes(modelHandle);
  const isBlacklisted = opt.incompatibleModel && opt.incompatibleModel.includes(modelHandle);
  return isWhitelisted && !isBlacklisted;
}

document.addEventListener('DOMContentLoaded', async () => {
  const container = document.getElementById('nor-builder-app');
  if (!container || !window.NORLYTH_BUILDER_CONFIG) return;

  const { storefrontAccessToken } = window.NORLYTH_BUILDER_CONFIG;
  if (!storefrontAccessToken) return;

  try {
    const products = await fetchAllBuilderProducts(storefrontAccessToken);
    window.builderData = processProducts(products);
    
    // Parse URL for ?model=handle
    const urlParams = new URLSearchParams(window.location.search);
    const initialModelHandle = urlParams.get('model');
    
    let defaultModel = window.builderData.models[0] || null;
    if (initialModelHandle) {
      const foundModel = window.builderData.models.find(m => m.handle === initialModelHandle);
      if (foundModel) defaultModel = foundModel;
    }

    // Initialize State
    window.builderState = {
      model: defaultModel,
      activeCategory: 'General Options',
      activeProperty: 'Model',
      selectedOptions: {}, // Format: { propName: productObj }
      totalPrice: 0,
      view: 'front',     // 'front' | 'rear'
      dexterity: 'right', // 'right' | 'left'
      bgColor: '#111',
      expandedFolders: new Set(['General Options']),
      customDecals: { front: null, back: null }
    };

    if (window.applyDefaults && window.builderState.model) {
      window.applyDefaults(window.builderState.model);
    }

    // Auto-Load Build Logic
    const buildParam = urlParams.get('build');
    if (buildParam) {
      const handles = buildParam.split(',');
      const buildModelHandle = handles[0];
      if (buildModelHandle) {
        const foundModel = window.builderData.models.find(m => m.handle === buildModelHandle);
        if (foundModel) window.builderState.model = foundModel;
      }
      
      handles.slice(1).forEach(handle => {
        let foundOpt = null;
        let foundCat = null;
        let foundProp = null;
        Object.keys(window.builderData.options).forEach(cat => {
          Object.keys(window.builderData.options[cat]).forEach(prop => {
            const opt = window.builderData.options[cat][prop].find(o => o.handle === handle);
            if (opt) {
               foundOpt = opt;
               foundCat = cat;
               foundProp = prop;
            }
          });
        });
        if (foundOpt) window.builderState.selectedOptions[foundProp] = foundOpt;
      });
      
      // Override dexterity if specifically present in selectedOptions
      const dextOpt = window.builderState.selectedOptions['Dexterity'];
      if (dextOpt && dextOpt.title.toLowerCase().includes('left')) {
        window.builderState.dexterity = 'left';
      } else {
        window.builderState.dexterity = 'right';
      }
    }

    renderApp(container);
  } catch (error) {
    container.innerHTML = `<div style="padding: 50px; text-align: center; color: red;">Failed to load builder data: ${error.message}</div>`;
  }
});

async function fetchAllBuilderProducts(token) {
  const CACHE_KEY = 'NORLYTH_BUILDER_CACHE_V2';
  const CACHE_TIME_KEY = 'NORLYTH_BUILDER_CACHE_TIME';
  const CACHE_EXPIRY_MS = 0; // 0 hours cache for development to avoid stale images

  const cachedData = localStorage.getItem(CACHE_KEY);
  const cacheTime = localStorage.getItem(CACHE_TIME_KEY);

  if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < CACHE_EXPIRY_MS) {
    try {
      console.log('📦 Loaded Builder Data from LocalStorage Cache');
      return JSON.parse(cachedData);
    } catch (e) {
      console.warn("Builder Cache corrupted, fetching fresh data...");
    }
  }

  console.log('⏳ Fetching fresh Builder Data via GraphQL API...');
  let allProducts = [], hasNextPage = true, cursor = null;
  while(hasNextPage) {
    const query = `
      query getProducts($cursor: String) {
        products(first: 250, after: $cursor, query: "tag:builder-option") {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id title handle productType
              images(first: 5) { edges { node { url(transform: {maxWidth: 2048}) } } }
              categoryMetafield: metafield(namespace: "custom", key: "builder_category") { value }
              propertyMetafield: metafield(namespace: "custom", key: "builder_property") { value }
              zindexMetafield: metafield(namespace: "custom", key: "builder_zindex") { value }
              zindexFrontMetafield: metafield(namespace: "custom", key: "builder_zindex_front") { value }
              zindexRearMetafield: metafield(namespace: "custom", key: "builder_zindex_rear") { value }
              compatibleMetafield: metafield(namespace: "custom", key: "compatible_model") { value }
              incompatibleMetafield: metafield(namespace: "custom", key: "incompatible_model") { value }
              uiTypeMetafield: metafield(namespace: "custom", key: "builder_ui_type") { value }
              defaultMetafield: metafield(namespace: "custom", key: "default_for_model") { value }
              sortMetafield: metafield(namespace: "custom", key: "builder_sort") { value }
              iconMetafield: metafield(namespace: "custom", key: "icon") {
                value
                reference {
                  ... on MediaImage { image { url(transform: {maxWidth: 500}) } }
                }
              }
              variants(first: 100) { edges { node { id title price { amount } image { url(transform: {maxWidth: 2048}) } selectedOptions { name value } } } }
            }
          }
        }
      }`;
    
    const response = await fetch(`/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query, variables: { cursor } })
    });
    const result = await response.json();
    if (result.errors) throw new Error(result.errors[0].message);

    allProducts.push(...result.data.products.edges.map(e => e.node));
    hasNextPage = result.data.products.pageInfo.hasNextPage;
    cursor = result.data.products.pageInfo.endCursor;
  }

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(allProducts));
    localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
    console.log('✅ Builder Data cached successfully to LocalStorage');
  } catch (e) {
    console.warn("Failed to cache builder data (likely quota exceeded)", e);
  }

  return allProducts;
}

function processProducts(products) {
  const models = [];
  const options = {};
  
  products.forEach(product => {
    const zValue = product.zindexFrontMetafield ? product.zindexFrontMetafield.value : (product.zindexMetafield ? product.zindexMetafield.value : null);
    const parsedZ = zValue ? parseInt(zValue) : 10;
    product.zindex = isNaN(parsedZ) ? 10 : parsedZ;
    const parsedZRear = product.zindexRearMetafield ? parseInt(product.zindexRearMetafield.value) : product.zindex;
    product.zindexRear = isNaN(parsedZRear) ? product.zindex : parsedZRear;
    product.compatibleModel = product.compatibleMetafield ? product.compatibleMetafield.value.trim() : null;
    product.incompatibleModel = product.incompatibleMetafield ? product.incompatibleMetafield.value.trim() : null;
    product.uiType = product.uiTypeMetafield ? product.uiTypeMetafield.value.trim().toLowerCase() : 'grid';
    product.defaultForModel = product.defaultMetafield ? product.defaultMetafield.value.toLowerCase().split(',').map(s => s.trim()) : [];
    
    // Extract icon URL (handles both direct text URL and Shopify File Reference)
    if (product.iconMetafield) {
       product.iconUrl = product.iconMetafield.reference?.image?.url || product.iconMetafield.value;
    } else {
       product.iconUrl = null;
    }

    if (product.productType === 'Guitar Model' || product.productType === 'Guitar') {
      models.push(product); 
      return;
    }
    const cat = product.categoryMetafield?.value;
    const prop = product.propertyMetafield?.value;
    if (cat && prop) {
      if (!options[cat]) options[cat] = {};
      if (!options[cat][prop]) options[cat][prop] = [];
      options[cat][prop].push(product);
    }
  });
  
  // Sort models and options
  models.sort((a, b) => {
  const sortA = a.sortMetafield && a.sortMetafield.value ? parseInt(a.sortMetafield.value, 10) : 9999;
  const sortB = b.sortMetafield && b.sortMetafield.value ? parseInt(b.sortMetafield.value, 10) : 9999;
  if (sortA !== sortB) return sortA - sortB;
  return a.title.localeCompare(b.title);
});

  
  Object.keys(options).forEach(cat => {
    Object.keys(options[cat]).forEach(prop => {
      options[cat][prop].sort((a, b) => {
        // 1. Sort by explicit metafield (builder_sort) if provided
        const sortA = a.sortMetafield && a.sortMetafield.value ? parseInt(a.sortMetafield.value, 10) : 9999;
        const sortB = b.sortMetafield && b.sortMetafield.value ? parseInt(b.sortMetafield.value, 10) : 9999;
        if (sortA !== sortB) return sortA - sortB;
        
        // 2. Sort by Price (Low to High)
        const priceA = parseFloat(a.variants?.edges[0]?.node?.price?.amount || 0);
        const priceB = parseFloat(b.variants?.edges[0]?.node?.price?.amount || 0);
        if (priceA !== priceB) return priceA - priceB;
        
        // 3. Fallback to Alphabetical
        return a.title.localeCompare(b.title);
      });
    });
  });
  
  return { models, options };
}

// ==========================================
// RENDERING
// ==========================================

function renderApp(container) {
  const data = window.builderData;
  if (data.models.length === 0 && Object.keys(data.options).length === 0) {
    container.innerHTML = `
      <div style="padding: 50px; text-align: center; color: #aaa; width: 100%; display: flex; flex-direction: column; justify-content: center;">
        <h2 style="color: #fff; margin-bottom: 20px;">No Builder Products Found</h2>
        <p>Please create products with Product Type "Guitar" and Options with Metafields in Shopify Admin.</p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="mobile-menu-toggle" onclick="toggleMobileMenu()">
      <span>Builder Menu</span>
      <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </div>
    <div class="builder-col-menu" id="col-menu"></div>
    <div class="builder-col-visualizer" id="col-visualizer"></div>
    <div class="builder-col-options" id="col-options"></div>
    <div class="mobile-cart-footer">
      <div class="price-area">
        <span class="cart-summary-title">CURRENT TOTAL</span>
        <span class="cart-summary-price" id="mobile-total-price">$0</span>
      </div>
      <button class="btn-add-to-cart" onclick="addToCart()">Add To Cart</button>
    </div>
  `;
  updateMenu();
  updateVisualizer();
  updateOptionsGrid();
}

function updateMenu() {
  const colMenu = document.getElementById('col-menu');
  const data = window.builderData;
  const state = window.builderState;

  if (!state.expandedFolders) {
    state.expandedFolders = new Set();
    if (state.activeCategory) {
      const parts = state.activeCategory.split('>').map(s => s.trim());
      let currentPath = '';
      parts.forEach(part => {
        currentPath = currentPath ? `${currentPath}-${part}` : part;
        state.expandedFolders.add(currentPath);
      });
    }
  }

  const tree = {};
  
  const allCats = new Set(Object.keys(data.options));
  allCats.add('General Options');
  const sortedCategories = Array.from(allCats).sort((a, b) => {
    let indexA = CATEGORY_ORDER.indexOf(a.split('>')[0].trim());
    let indexB = CATEGORY_ORDER.indexOf(b.split('>')[0].trim());
    if (indexA === -1) indexA = 999;
    if (indexB === -1) indexB = 999;
    return indexA - indexB;
  });

  sortedCategories.forEach(fullCatPath => {
    let hasValidProps = false;
    if (data.options[fullCatPath]) {
      hasValidProps = Object.keys(data.options[fullCatPath]).some(prop => {
        return data.options[fullCatPath][prop].some(opt => isOptionCompatible(opt, state.model?.handle));
      });
    }
    if (fullCatPath === 'General Options') hasValidProps = true; // Model is always available!
    
    if (!hasValidProps) return;

    const parts = fullCatPath.split('>').map(s => s.trim());
    let current = tree;
    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = {
          name: part,
          children: {},
          fullPath: null
        };
      }
      if (index === parts.length - 1) {
        current[part].fullPath = fullCatPath;
      }
      current = current[part].children;
    });
  });

  function renderTree(nodeObj, level = 0, parentId = '') {
    let html = '';
    const keys = Object.keys(nodeObj);
    
    keys.forEach(key => {
      const node = nodeObj[key];
      const folderId = parentId ? `${parentId}__${key}` : key;
      const isExpanded = state.expandedFolders.has(folderId);
      
      html += `
        <div class="menu-category ${isExpanded ? 'active' : ''}">
          <div class="menu-category-header" onclick="toggleFolder('${folderId}')" style="padding-left: ${20 + level * 10}px">
            ${node.name}
            <span>${isExpanded ? '▼' : '▶'}</span>
          </div>
          <div class="menu-category-content">
      `;
      
      if (node.fullPath || Object.keys(node.children).length > 0) {
        let unifiedItems = [];
        
        if (node.fullPath && data.options[node.fullPath]) {
          const props = Object.keys(data.options[node.fullPath]).filter(prop => {
            return data.options[node.fullPath][prop].some(opt => isOptionCompatible(opt, state.model?.handle));
          });
          props.forEach(p => unifiedItems.push({ type: 'property', name: p }));
        }

        if (node.name === 'General Options') {
           unifiedItems.push({ type: 'property', name: 'Model' });
        }

        if (node.children) {
          Object.keys(node.children).forEach(childKey => {
            unifiedItems.push({ type: 'category', name: childKey, node: node.children[childKey] });
          });
        }
        
        if (node.name === 'Body Finish') {
           unifiedItems.push({ type: 'custom_finish', name: 'Custom Finish' });
        }

        unifiedItems.sort((a, b) => {
          const normalizedOrder = PROPERTY_ORDER.map(p => p.trim().toLowerCase());
          let indexA = normalizedOrder.indexOf(a.name.trim().toLowerCase());
          let indexB = normalizedOrder.indexOf(b.name.trim().toLowerCase());
          if (indexA === -1) indexA = 999;
          if (indexB === -1) indexB = 999;
          return indexA - indexB;
        });

        unifiedItems.forEach(item => {
          if (item.type === 'property') {
            const isActiveProp = (state.activeProperty === item.name && state.activeCategory === node.fullPath);
            html += `<div class="menu-property ${isActiveProp ? 'active' : ''}" 
                          onclick="setActiveProperty('${node.fullPath}', '${item.name}')"
                          style="padding-left: ${20 + (level+1) * 10}px">
              ${item.name}
            </div>`;
          } else if (item.type === 'category') {
            const childObj = {};
            childObj[item.name] = item.node;
            html += renderTree(childObj, level + 1, folderId);
          } else if (item.type === 'custom_finish') {
            const isCustomFinishActive = (state.activeProperty === 'Custom Finish' && state.activeCategory === node.fullPath);
            html += `<div class="menu-property ${isCustomFinishActive ? 'active' : ''}" 
                          onclick="setActiveProperty('${node.fullPath}', 'Custom Finish')"
                          style="padding-left: ${20 + (level+1) * 10}px; color: var(--COLOR-PRIMARY);">
              Custom Finish
            </div>`;
          }
        });
      }
      
      html += `
          </div>
        </div>
      `;
    });
    return html;
  }

  const menuHeaderHTML = `
    <div class="mobile-menu-header">
      <button class="btn-close-menu" onclick="toggleMobileMenu()">
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Guitar
      </button>
    </div>
  `;
  
  const menuFooterHTML = `
    <div class="builder-menu-footer" style="padding: 20px; text-align: center; font-size: 11px; color: #888; border-top: 1px solid #333; margin-top: 40px;">
      <p style="margin: 0 0 5px 0;">* All orders include a free soft case</p>
      <p style="margin: 0 0 15px 0;">* No changes can be made after your order has been placed</p>
      <p style="margin: 0 0 5px 0; font-size: 12px; color: #eee;">Not seeing what you're looking for?</p>
      <p style="margin: 0 0 20px 0; font-size: 12px; color: #eee;">Interested in customizing your build further?</p>
      <p style="margin: 0 0 10px 0; color: #ccc;">Give us a call <strong>BEFORE</strong> placing your order</p>
      <p style="margin: 0 0 5px 0; font-size: 14px; color: #fff; font-weight: bold;">+85251401837</p>
      <p style="margin: 20px 0 5px 0; color: #ccc;">International customers please email</p>
      <a href="mailto:builder@norlyth.com" style="color: var(--COLOR-PRIMARY); text-decoration: none;">builder@norlyth.com</a>
    </div>
  `;

  const exportExpanded = state.expandedFolders.has('export_load');
  const exportHTML = `
    <div class="menu-category ${exportExpanded ? 'active' : ''}">
      <div class="menu-category-header" onclick="toggleFolder('export_load')" style="padding-left: 20px">
        Export/Load
        <span>${exportExpanded ? '▼' : '▶'}</span>
      </div>
      <div class="menu-category-content">
        <div class="menu-property" onclick="exportShareBuild()" style="padding-left: 30px">Share Saved Build</div>
        <div class="menu-property" onclick="exportSpecs()" style="padding-left: 30px">Export Specs</div>
        <div class="menu-property" onclick="exportImage()" style="padding-left: 30px">Export Image</div>
      </div>
    </div>
  `;

  colMenu.innerHTML = menuHeaderHTML + renderTree(tree) + exportHTML + menuFooterHTML;
}

function getActiveImageIndex() {
  const state = window.builderState;
  return state.view === 'front' ? 0 : 1;
}

function getLayersForView(view) {
  const state = window.builderState;
  const imageIndex = view === 'front' ? 0 : 1;
  const currentModelTitle = state.model ? state.model.title.toLowerCase() : '';
  const currentModelHandle = state.model ? state.model.handle.toLowerCase() : '';
  const currentViewTitle = view === 'front' ? 'front' : 'rear';

  let layers = [];
  
  if (state.model && (!window.NORLYTH_BUILDER_CONFIG || !window.NORLYTH_BUILDER_CONFIG.hideModelBaseImage)) {
    const modelImg = state.model.images.edges[imageIndex]?.node?.url || state.model.images.edges[0]?.node?.url;
    if (modelImg) {
      const currentZ = view === 'rear' ? (state.model.zindexRear || 0) : (state.model.zindex || 0);
      layers.push({ id: 'model', url: modelImg, zindex: currentZ });
    }
  }

  Object.entries(state.selectedOptions).forEach(([prop, opt]) => {
    if (prop.toLowerCase() === 'dexterity') return;
    
    let optImg = null;
    const hasCustomVariants = opt.variants && opt.variants.edges && opt.variants.edges.length > 1;

    if (hasCustomVariants) {
      let isMultidimensional = false;
      const matchedVariant = opt.variants.edges.map(e => e.node).find(v => {
        let isMatch = true;
        
        if (v.selectedOptions) {
           for (const o of v.selectedOptions) {
              const optName = o.name.toLowerCase();
              const optVal = o.value.toLowerCase();
              
              if (optName === 'title' && optVal === 'default title') continue;
              
              const splitNames = optName.split('+').map(s => s.trim());
              const splitVals = optVal.split('+').map(s => s.trim());
              
              for (let i = 0; i < splitNames.length; i++) {
                 const subName = splitNames[i];
                 const subVal = splitVals[i] || '';
                 
                 if (subName === 'model') {
                    isMultidimensional = true;
                    if (subVal !== currentModelTitle && subVal !== 'all' && subVal !== 'generic') isMatch = false;
                 } else if (subName === 'view') {
                    isMultidimensional = true;
                    if (subVal !== currentViewTitle) isMatch = false;
                 } else {
                    const targetPropKey = Object.keys(state.selectedOptions).find(k => k.toLowerCase() === subName);
                    if (targetPropKey) {
                       isMultidimensional = true;
                       const targetSelectedProduct = state.selectedOptions[targetPropKey];
                       if (targetSelectedProduct.title.toLowerCase() !== subVal) {
                          isMatch = false;
                       }
                    } else {
                       // Unknown dimension (e.g. spelling typo or standard variant). We fail the match to be safe.
                       isMatch = false;
                    }
                 }
              }
           }
        }
        return isMatch;
      });

      if (matchedVariant) {
        optImg = matchedVariant.image ? matchedVariant.image.url : null;
      } else if (isMultidimensional) {
        // Strict rule: It is a multidimensional product, but no variant matched the current state.
        // Therefore, this state (e.g. Okoume) is not supported by this product. Show nothing!
        optImg = null;
      } else {
        // Not a multidimensional product. Fallback to default index matching.
        optImg = opt.images.edges[imageIndex]?.node?.url;
      }
    } else {
      // Strict index matching for non-variant products
      optImg = opt.images.edges[imageIndex]?.node?.url;
    }

    if (optImg) {
      const currentZ = view === 'rear' ? opt.zindexRear : opt.zindex;
      layers.push({ id: prop, url: optImg, zindex: currentZ });
    }
  });

  layers.sort((a, b) => a.zindex - b.zindex);
  return layers;
}

function updateVisualizer() {
  const colVis = document.getElementById('col-visualizer');
  const state = window.builderState;
  const price = calculateTotalPrice(); 
  const layers = getLayersForView(state.view);
  const flipClass = state.dexterity === 'left' ? 'is-left-handed' : '';
  colVis.style.backgroundColor = state.bgColor;

  colVis.innerHTML = `
    <div class="visualizer-top-bar">
      <div class="view-toggles-container">
        <div class="view-toggles">
          <button class="view-btn ${state.view === 'front' ? 'active' : ''}" onclick="setView('front')">Front</button>
          <button class="view-btn ${state.view === 'rear' ? 'active' : ''}" onclick="setView('rear')">Rear</button>
        </div>
        <div style="display: flex; flex-direction: row; gap: 12px; align-items: flex-start;">
          <div class="bg-toggle-wrapper">
            <button class="btn-builder-reset" onclick="toggleBgMenu()" title="Change Background">
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2v20a10 10 0 1 0 0-20z"></path>
                <circle cx="12" cy="12" r="10" fill="none"></circle>
              </svg>
            </button>
            <div class="bg-menu-dropdown" id="bg-menu-dropdown">
              <div class="bg-swatch" style="background: #111;" onclick="setBgColor('#111')"></div>
              <div class="bg-swatch" style="background: #888;" onclick="setBgColor('#888')"></div>
              <div class="bg-swatch" style="background: #fff;" onclick="setBgColor('#fff')"></div>
            </div>
          </div>
          <button class="btn-builder-reset" onclick="resetBuilder()" title="Clear All Selections">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>
      </div>
      <div class="top-bar-price">
        <div class="top-bar-price-label">CURRENT TOTAL</div>
        <div class="top-bar-price-value">$${calculateTotalPrice().toLocaleString()}</div>
        <button class="btn-add-to-cart" onclick="addToCart()">Add To Cart</button>
      </div>
    </div>
    <div class="visualizer-image-container ${flipClass}">
      ${layers.map(layer => `<img class="visualizer-layer" src="${layer.url}" style="z-index: ${layer.zindex};">`).join('')}
      
      ${(() => {
        const decal = state.customDecals[state.view];
        if (!decal) return '';
        const maskLayer = layers.find(l => l.id.toLowerCase().includes('body')) 
                       || layers.find(l => l.zindex > 0 && l.zindex < 20) 
                       || layers.find(l => l.id.toLowerCase().includes('wood')) 
                       || layers.find(l => l.id === 'model');
        if (!maskLayer) return '';
        
        const decalZ = maskLayer.zindex + 1;
        const w = decal.width || 300;
        const h = decal.height || 300;
        const transformStr = `translate(${decal.x}px, ${decal.y}px) scale(${decal.scale}) rotate(${decal.rotation}deg)`;
        const baseStyles = `position: absolute; top: 50%; left: 50%; margin-left: -${w/2}px; margin-top: -${h/2}px; width: ${w}px !important; height: ${h}px !important; max-width: none !important; max-height: none !important; transform-origin: center center; transform: ${transformStr};`;
        
        // Inverse scale to maintain visual size of border and handles regardless of decal.scale
        const invScale = 1 / decal.scale;
        const borderThickness = Math.max(1, 2 * invScale);
        const handleSize = 28 * invScale;
        const handleOffset = -35 * invScale;
        const handleOffsetScale = -14 * invScale;

        return `
          <div class="decal-mask-container visualizer-layer" style="z-index: ${decalZ}; pointer-events:none; -webkit-mask-image: url('${maskLayer.url}'); -webkit-mask-size: contain; -webkit-mask-position: center; -webkit-mask-repeat: no-repeat; mask-image: url('${maskLayer.url}'); mask-size: contain; mask-position: center; mask-repeat: no-repeat;">
            <img class="decal-image" src="${decal.dataUrl}" style="${baseStyles}">
          </div>
          <div class="decal-controls-overlay visualizer-layer" style="z-index: 9999; pointer-events: none;">
            <div class="decal-bounding-box" style="${baseStyles} border: ${borderThickness}px solid rgba(255,255,255,0.7); box-shadow: 0 0 0 ${1*invScale}px rgba(0,0,0,0.3); cursor: move; pointer-events: auto;">
              <!-- Rotate handle -->
              <div class="decal-handle decal-handle-rotate" style="position: absolute; bottom: ${handleOffset}px; left: 50%; transform: translateX(-50%); width: ${handleSize}px; height: ${handleSize}px; background: #fff; border: ${1*invScale}px solid #6b52ff; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 ${2*invScale}px ${4*invScale}px rgba(0,0,0,0.2);">
                 <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" stroke="#6b52ff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.59-9.21l5.82-5.82"/></svg>
              </div>
              <!-- Scale handle -->
              <div class="decal-handle decal-handle-scale" style="position: absolute; bottom: ${handleOffsetScale}px; right: ${handleOffsetScale}px; width: ${handleSize}px; height: ${handleSize}px; background: #fff; border: ${1*invScale}px solid #6b52ff; border-radius: 50%; cursor: nwse-resize; display: flex; align-items: center; justify-content: center; box-shadow: 0 ${2*invScale}px ${4*invScale}px rgba(0,0,0,0.2);">
                 <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" stroke="#6b52ff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>
              </div>
            </div>
          </div>
        `;
      })()}
    </div>
    <div class="builder-canvas-footer" style="padding: 10px 20px 20px 20px; text-align: center; font-size: 10px; color: #666; pointer-events: none; z-index: 10;">
      <p style="margin: 0 0 4px 0;">Graphic Representation Only - Actual Product May Differ</p>
      <p style="margin: 0;">Finish colors are matched under studio lighting - Standard home lighting conditions may cause finishes to appear darker than Virtual Builder renderings</p>
    </div>
  `;

  if (window.initDecalDrag) setTimeout(window.initDecalDrag, 50);

  const mobilePrice = document.getElementById('mobile-total-price');
  if (mobilePrice) {
    mobilePrice.innerText = '$' + price.toLocaleString();
  }
}

function updateOptionsGrid() {
  const colOptions = document.getElementById('col-options');
  const state = window.builderState;
  const data = window.builderData;

  if (state.activeCategory === 'Body Options > Body Finish' && state.activeProperty === 'Custom Finish') {
     // Preload masks to speed up Add To Cart
     const frontUrl = getMaskLayerUrl('front');
     if (frontUrl) new Image().src = frontUrl;
     const rearUrl = getMaskLayerUrl('rear');
     if (rearUrl) new Image().src = rearUrl;
     
     colOptions.innerHTML = renderCustomFinishUI();
     return;
  }

  if (state.activeProperty === 'Model') {
      let html = `
        <div class="options-header">
          <h3>Model</h3>
          <button class="btn-close-options" onclick="closeMobileOptions()">&times;</button>
        </div>
        <div class="options-grid-container">
          <div class="options-grid">
      `;
      html += data.models.map(opt => {
        const price = parseFloat(opt.variants?.edges[0]?.node?.price?.amount || 0);
        const isSelected = state.model?.id === opt.id;
        const thumbUrl = opt.iconUrl || opt.images.edges[0]?.node?.url;
        
        return `
        <div class="option-card ${isSelected ? 'selected' : ''}" onclick="selectOption('Model', '${opt.id}')">
          <div class="option-thumb">
            ${thumbUrl ? `<img src="${thumbUrl}" alt="${opt.title}">` : ''}
          </div>
          <div class="option-title">${opt.title}</div>
          <div class="option-price">${price === 0 ? 'Base Price' : 'Starts at $' + price}</div>
        </div>
        `;
      }).join('');
      html += `</div></div>`;
      colOptions.innerHTML = html;
      return;
  }

  if (state.activeCategory && state.activeProperty && data.options[state.activeCategory]) {
    let products = data.options[state.activeCategory][state.activeProperty] || [];
    // Filter compatible options (Whitelist & Blacklist hybrid check)
    products = products.filter(opt => isOptionCompatible(opt, state.model?.handle));
    
    // Check if the property expects a selector type UI
    const propertyName = state.activeProperty.replace(/([A-Z])/g, ' $1').trim();
    const uiType = products[0]?.uiType || 'grid';

    let html = `
      <div class="options-header">
        <h3>${propertyName}</h3>
        <button class="btn-close-options" onclick="closeMobileOptions()">&times;</button>
      </div>
      <div class="options-grid-container">
    `;

    if (uiType === 'list') {
      html += `<div class="builder-options-list">`;
      html += products.map(opt => {
        const variant = getActiveVariant(opt, state.model) || opt.variants.edges[0]?.node;
        const price = parseFloat(variant?.price?.amount || 0);
        const isSelected = state.selectedOptions[state.activeProperty]?.id === opt.id;
        
        return `
        <div class="option-list-item ${isSelected ? 'selected' : ''}" onclick="selectOption('${state.activeProperty}', '${opt.id}')">
          <label class="nor-toggle" onclick="event.preventDefault()">
            <input type="checkbox" ${isSelected ? 'checked' : ''} tabindex="-1">
            <span class="nor-slider"></span>
          </label>
          <span class="option-list-title">${opt.title}</span>
          <span class="option-list-price">${price === 0 ? 'No Charge' : '+$' + price}</span>
        </div>
        `;
      }).join('');
      html += `</div>`;
    } else {
      html += `<div class="options-grid">`;
      html += products.map(opt => {
        const variant = getActiveVariant(opt, state.model) || opt.variants.edges[0]?.node;
        const price = parseFloat(variant?.price?.amount || 0);
        const isSelected = state.selectedOptions[state.activeProperty]?.id === opt.id;
        const thumbUrl = opt.iconUrl || opt.images.edges[0]?.node?.url;
        
        return `
        <div class="option-card ${isSelected ? 'selected' : ''}" onclick="selectOption('${state.activeProperty}', '${opt.id}')">
          <div class="option-thumb">
            ${thumbUrl ? `<img src="${thumbUrl}" alt="${opt.title}">` : ''}
          </div>
          <div class="option-title">${opt.title}</div>
          <div class="option-price">${price === 0 ? 'No Charge' : '+$' + price}</div>
        </div>
        `;
      }).join('');
      html += `</div>`;
    }

    html += `</div>`;
    colOptions.innerHTML = html;
  } else {
    colOptions.innerHTML = '<div style="padding: 20px; color: #888;">Select an option from the left menu.</div>';
  }
}

// ==========================================
// ACTIONS
// ==========================================

window.toggleFolder = function(folderId) {
  const state = window.builderState;
  if (!state.expandedFolders) state.expandedFolders = new Set();
  if (state.expandedFolders.has(folderId)) {
    state.expandedFolders.delete(folderId);
  } else {
    // 1. Determine parent path
    const parts = folderId.split('__');
    parts.pop(); 
    const parentPath = parts.join('__');
    
    // 2. Find siblings (folders that share the exact same parent path)
    const siblingsToClose = Array.from(state.expandedFolders).filter(openFolder => {
      const openParts = openFolder.split('__');
      openParts.pop();
      return openParts.join('__') === parentPath;
    });
    
    // 3. Delete siblings and all their descendants
    siblingsToClose.forEach(sibling => {
      Array.from(state.expandedFolders).forEach(f => {
        if (f === sibling || f.startsWith(sibling + '__')) {
          state.expandedFolders.delete(f);
        }
      });
    });

    // 4. Open the clicked folder
    state.expandedFolders.add(folderId);
  }
  updateMenu();
};

window.toggleMobileMenu = function() {
  const menu = document.getElementById('col-menu');
  const toggle = document.querySelector('.mobile-menu-toggle');
  if (menu && toggle) {
    menu.classList.toggle('open');
    toggle.classList.toggle('open');
  }
};

window.closeMobileOptions = function() {
  const options = document.getElementById('col-options');
  if (options) options.classList.remove('open');
};

window.openMobileOptions = function() {
  const options = document.getElementById('col-options');
  if (options) options.classList.add('open');
};

window.setActiveCategory = function(cat) {
  window.builderState.activeCategory = cat;
  window.builderState.activeProperty = Object.keys(window.builderData.options[cat])[0] || null;
  updateMenu();
  updateOptionsGrid();
};

window.setActiveProperty = function(cat, prop) {
  window.builderState.activeCategory = cat;
  window.builderState.activeProperty = prop;
  updateMenu();
  updateOptionsGrid();
  if (window.innerWidth <= 768) {
    window.openMobileOptions();
  }
};

window.selectOption = function(prop, productId) {
  const state = window.builderState;
  const data = window.builderData;
  
  if (prop === 'Model') {
     const foundModel = data.models.find(m => m.id === productId);
     if (foundModel && (!state.model || state.model.id !== productId)) {
        state.model = foundModel;
        // Reset builder state for the new model
        state.selectedOptions = {};
        state.customDecals = { front: null, rear: null };
        state.totalPrice = 0;
        state.view = 'front';
        state.dexterity = 'right';
        
        if (window.applyDefaults) {
           window.applyDefaults(state.model);
        }
        
        updateMenu();
        updateVisualizer();
        updateOptionsGrid();
        if (window.innerWidth <= 768) {
           window.closeMobileOptions();
        }
     }
     return;
  }

  // Find product object
  const product = data.options[state.activeCategory][prop].find(p => p.id === productId);
  
  if (product) {
    state.selectedOptions[prop] = product;
    
    // General Mutually Exclusive Engine
    if (MUTUALLY_EXCLUSIVE_RULES[prop]) {
       MUTUALLY_EXCLUSIVE_RULES[prop].forEach(conflictProp => {
          if (conflictProp === 'Custom Finish') {
             state.customDecals = { front: null, rear: null };
          } else {
             delete state.selectedOptions[conflictProp];
          }
       });
    }
    
    // Check if this property dictates Dexterity
    if (prop.toLowerCase().includes('dexterity')) {
      if (product.title.toLowerCase().includes('left')) state.dexterity = 'left';
      else state.dexterity = 'right';
    }
    
    updateOptionsGrid();
    updateVisualizer();
    if (window.innerWidth <= 768) {
      window.closeMobileOptions();
    }
  }
};

window.setDexterity = function(val) {
  window.builderState.dexterity = val;
  updateVisualizer();
};

window.toggleBgMenu = function() {
  const menu = document.getElementById('bg-menu-dropdown');
  if (menu) {
    menu.classList.toggle('show');
  }
};

window.setBgColor = function(color) {
  window.builderState.bgColor = color;
  const menu = document.getElementById('bg-menu-dropdown');
  if (menu) menu.classList.remove('show');
  const vis = document.getElementById('col-visualizer');
  vis.style.background = color;
  if (color === '#fff') {
    vis.classList.add('bg-light');
  } else {
    vis.classList.remove('bg-light');
  }
};

// Close bg dropdown when clicking outside
document.addEventListener('click', function(e) {
  const menu = document.getElementById('bg-menu-dropdown');
  if (menu && menu.classList.contains('show')) {
    if (!e.target.closest('.bg-toggle-wrapper')) {
      menu.classList.remove('show');
    }
  }
});

window.setView = function(view) {
  window.builderState.view = view;
  updateVisualizer();
  updateOptionsGrid();
};

window.applyDefaults = function(model) {
  if (!model) return;
  const state = window.builderState;
  const data = window.builderData;
  const modelHandle = model.handle.toLowerCase();
  
  Object.keys(data.options).forEach(cat => {
    Object.keys(data.options[cat]).forEach(prop => {
      const defaultProduct = data.options[cat][prop].find(p => p.defaultForModel && p.defaultForModel.includes(modelHandle));
      if (defaultProduct) {
        state.selectedOptions[prop] = defaultProduct;
      }
    });
  });
};

window.resetBuilder = function(force = false) {
  if (force || confirm("Are you sure you want to clear all selections and start over?")) {
    window.builderState.selectedOptions = {};
    window.builderState.customDecals = { front: null, rear: null };
    window.builderState.totalPrice = 0;
    window.builderState.view = 'front';
    window.builderState.dexterity = 'right';
    window.applyDefaults(window.builderState.model);
    updateOptionsGrid();
    updateVisualizer();
  }
};

function getActiveVariant(opt, model) {
  if (!opt || !opt.variants || !opt.variants.edges || opt.variants.edges.length === 0) return null;
  const isDefaultForCurrentModel = model && opt.defaultForModel && opt.defaultForModel.includes(model.handle.toLowerCase());
  
  if (isDefaultForCurrentModel) {
    let noChargeVariant = opt.variants.edges.find(e => 
      e.node.title.toLowerCase().includes('no charge') || 
      parseFloat(e.node.price.amount || 0) === 0
    );
    return noChargeVariant ? noChargeVariant.node : opt.variants.edges[0].node;
  } else {
    let paidVariant = opt.variants.edges.find(e => 
      e.node.title.toLowerCase().includes('default') && parseFloat(e.node.price.amount || 0) > 0
    );
    if (!paidVariant) {
      paidVariant = opt.variants.edges.find(e => parseFloat(e.node.price.amount || 0) > 0);
    }
    return paidVariant ? paidVariant.node : opt.variants.edges[0].node;
  }
}

function calculateTotalPrice() {
  const state = window.builderState;
  let total = 0;
  
  if (state.model && state.model.variants.edges.length > 0) {
    total += parseFloat(state.model.variants.edges[0].node.price.amount || 0);
  }

  Object.values(state.selectedOptions).forEach(opt => {
    const activeVar = getActiveVariant(opt, state.model) || opt.variants.edges[0]?.node;
    const p = parseFloat(activeVar?.price?.amount || 0);
    total += p;
  });

  // Add custom finish fee if either front or rear decal exists
  if (state.customDecals.front || state.customDecals.rear) {
    if (window.NORLYTH_BUILDER_CONFIG && window.NORLYTH_BUILDER_CONFIG.customFinishFeePrice) {
      // Liquid price is in cents
      total += parseFloat(window.NORLYTH_BUILDER_CONFIG.customFinishFeePrice) / 100;
    }
  }

  return total;
}

function getMaskLayerUrl(view) {
  const state = window.builderState;
  const currentModelTitle = state.model ? state.model.title.toLowerCase() : '';
  const currentViewTitle = view === 'front' ? 'front' : 'rear';
  const imageIndex = state.dexterity === 'left' ? 1 : 0;
  
  const bodyWoodOpt = state.selectedOptions['Body Wood'];
  if (bodyWoodOpt) {
    let optImg = null;
    const hasCustomVariants = bodyWoodOpt.variants && bodyWoodOpt.variants.edges && bodyWoodOpt.variants.edges.length > 1;

    if (hasCustomVariants) {
      const matchedVariant = bodyWoodOpt.variants.edges.map(e => e.node).find(v => {
        let isMatch = true;
        if (v.selectedOptions) {
           for (const o of v.selectedOptions) {
              const optName = o.name.toLowerCase();
              const optVal = o.value.toLowerCase();
              if (optName === 'title' && optVal === 'default title') continue;
              
              const splitNames = optName.split('+').map(s => s.trim());
              const splitVals = optVal.split('+').map(s => s.trim());
              
              for (let i = 0; i < splitNames.length; i++) {
                 const subName = splitNames[i];
                 const subVal = splitVals[i] || '';
                 
                 if (subName === 'model') {
                    if (subVal !== currentModelTitle && subVal !== 'all' && subVal !== 'generic') isMatch = false;
                 } else if (subName === 'view') {
                    if (subVal !== currentViewTitle) isMatch = false;
                 } else {
                    const targetPropKey = Object.keys(state.selectedOptions).find(k => k.toLowerCase() === subName);
                    if (targetPropKey) {
                       const targetSelectedProduct = state.selectedOptions[targetPropKey];
                       if (targetSelectedProduct.title.toLowerCase() !== subVal) {
                          isMatch = false;
                       }
                    } else {
                       isMatch = false;
                    }
                 }
              }
           }
        }
        return isMatch;
      });
      if (matchedVariant && matchedVariant.image) optImg = matchedVariant.image.url;
    } else {
      optImg = bodyWoodOpt.images.edges[imageIndex]?.node?.url || bodyWoodOpt.images.edges[0]?.node?.url;
    }
    if (optImg) return optImg;
  }
  
  if (state.model) {
    return state.model.images.edges[imageIndex]?.node?.url || state.model.images.edges[0]?.node?.url;
  }
  return null;
}

async function generateMaskedDecalBlob(view) {
  const decal = window.builderState.customDecals[view];
  if (!decal) return null;
  
  const maskLayer = getMaskLayerUrl(view);
  
  if (!maskLayer) return null;

  return new Promise((resolve, reject) => {
    const container = document.querySelector('.decal-mask-container');
    if (!container) return resolve(null);
    const rect = container.getBoundingClientRect();
    const CW = rect.width;
    const CH = rect.height;

    const maskImg = new Image();
    maskImg.crossOrigin = "Anonymous";
    maskImg.onload = () => {
      const IW = maskImg.naturalWidth;
      const IH = maskImg.naturalHeight;
      
      const scaleRatio = Math.min(CW / IW, CH / IH);
      const RW = IW * scaleRatio;
      const RH = IH * scaleRatio;
      const offsetX = (CW - RW) / 2;
      const offsetY = (CH - RH) / 2;

      const Cx = CW / 2 + decal.x;
      const Cy = CH / 2 + decal.y;

      const MaskCx = Cx - offsetX;
      const MaskCy = Cy - offsetY;

      const TargetCx = MaskCx / scaleRatio;
      const TargetCy = MaskCy / scaleRatio;
      const TargetScale = decal.scale / scaleRatio;

      const canvas = document.createElement('canvas');
      canvas.width = IW;
      canvas.height = IH;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(maskImg, 0, 0, IW, IH);
      ctx.globalCompositeOperation = 'source-in';

      const decalImg = new Image();
      decalImg.crossOrigin = "Anonymous";
      decalImg.onload = () => {
        ctx.translate(TargetCx, TargetCy);
        ctx.rotate(decal.rotation * Math.PI / 180);
        ctx.scale(TargetScale, TargetScale);
        ctx.drawImage(decalImg, -decal.width / 2, -decal.height / 2, decal.width, decal.height);
        
        canvas.toBlob(blob => {
          resolve(blob);
        }, 'image/webp', 0.8);
      };
      decalImg.onerror = () => resolve(null);
      decalImg.src = decal.dataUrl;
    };
    maskImg.onerror = () => resolve(null);
    maskImg.src = maskLayer;
  });
}

window.addToCart = async function() {
  const state = window.builderState;
  const jsonItems = [];

  const btn = document.querySelector('.btn-add-to-cart');
  const originalText = btn.innerText;
  btn.innerText = "Adding...";
  btn.disabled = true;

  let cartOverlay = document.createElement('div');
  cartOverlay.innerHTML = `
    <div style="position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.85); z-index:99999; display:flex; flex-direction:column; justify-content:center; align-items:center; color:#fff;">
      <div class="spinner" style="border: 4px solid rgba(255,255,255,0.1); border-left-color: var(--COLOR-PRIMARY, #00aeef); border-radius: 50%; width: 50px; height: 50px; animation: spin 1s linear infinite; margin-bottom: 20px;"></div>
      <h2 style="margin: 0 0 10px 0; font-size: 22px; font-weight: 600;">Building your guitar...</h2>
      <p style="color: #aaa; font-size: 14px;">Adding components to cart, please wait.</p>
    </div>
  `;
  document.body.appendChild(cartOverlay);

  try {
    const buildId = 'build_' + Date.now().toString();
    const rootUrl = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
    
    // 1. Generate Masks Concurrently
    const [frontBlob, rearBlob] = await Promise.all([
      generateMaskedDecalBlob('front'),
      generateMaskedDecalBlob('rear')
    ]);
    
    // 2. Setup Main Guitar Promise (FormData)
    let mainGuitarPromise = null;
    if (state.model && state.model.variants.edges[0]) {
      const formData = new FormData();
      formData.append('id', parseInt(state.model.variants.edges[0].node.id.split('/').pop()));
      formData.append('quantity', 1);
      
      formData.append('properties[_build_id]', buildId);
      formData.append('properties[_is_build_parent]', 'true');
      
      if (frontBlob) formData.append('properties[_Pure_Front_Decal]', frontBlob, 'front_decal.webp');
      if (rearBlob) formData.append('properties[_Pure_Rear_Decal]', rearBlob, 'rear_decal.webp');

      mainGuitarPromise = fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        body: formData
      });
    } else {
      alert("No model selected!");
      btn.innerText = originalText;
      btn.disabled = false;
      document.body.removeChild(cartOverlay);
      return;
    }

    // 3. Assemble other parts
    Object.entries(state.selectedOptions).forEach(([prop, opt]) => {
      const activeVar = getActiveVariant(opt, state.model) || (opt.variants && opt.variants.edges[0] ? opt.variants.edges[0].node : null);
      if (activeVar) {
        jsonItems.push({ 
          id: parseInt(activeVar.id.split('/').pop()), 
          quantity: 1,
          properties: {
            '_build_id': buildId,
            '_build_prop_name': prop
          }
        });
      }
    });

    // 4. Add Fee Product if needed
    if ((frontBlob || rearBlob) && window.NORLYTH_BUILDER_CONFIG && window.NORLYTH_BUILDER_CONFIG.customFinishFeeVariantId) {
      const feeIdStr = window.NORLYTH_BUILDER_CONFIG.customFinishFeeVariantId.toString();
      let feeId = parseInt(feeIdStr.split('/').pop());
      if (feeId) {
        jsonItems.push({
          id: feeId,
          quantity: 1,
          properties: {
            '_build_id': buildId,
            'For': 'Custom Finish Upcharge'
          }
        });
      }
    }

    // 5. Setup JSON items Promise
    let jsonItemsPromise = null;
    if (jsonItems.length > 0) {
      jsonItemsPromise = fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: jsonItems })
      });
    }
    
    // 6. Execute fetches in parallel
    const promises = [mainGuitarPromise];
    if (jsonItemsPromise) promises.push(jsonItemsPromise);
    
    const results = await Promise.all(promises);
    
    // Check results
    for (const res of results) {
       if (!res.ok) {
           const errorData = await res.json();
           throw new Error(errorData.description || 'Failed to add items to cart');
       }
    }
    
    // Redirect
    window.location.href = rootUrl + 'cart';

  } catch (err) {
    console.error(err);
    alert('Failed to add items to cart: ' + err.message);
    btn.innerText = originalText;
    btn.disabled = false;
    document.body.removeChild(cartOverlay);
  }
};

// ==========================================
// EXPORT / LOAD MODULE
// ==========================================

function exportShareBuild() {
  const state = window.builderState;
  if (!state.model) return;
  
  const handles = [state.model.handle];
  Object.values(state.selectedOptions).forEach(opt => {
    if (opt && opt.handle) handles.push(opt.handle);
  });
  
  const buildStr = handles.join(',');
  const url = window.location.origin + window.location.pathname + '?build=' + buildStr;
  
  showExportModal('Share Saved Build', `
    <p style="margin-bottom: 15px; color: #ccc; font-size: 14px;">Copy this link to share or save your build. Anyone who opens it will instantly see your exact guitar configuration.</p>
    <div style="display: flex; gap: 10px;">
      <input type="text" id="export-link-input" value="${url}" readonly style="flex: 1; padding: 12px; background: #222; border: 1px solid #444; color: #fff; border-radius: 4px; font-size: 14px; outline: none;">
      <button class="btn-add-to-cart" style="width: auto; padding: 0 20px; font-size: 14px; margin-top: 0; background: var(--COLOR-PRIMARY);" onclick="copyToClipboard('export-link-input', this)">Copy Link</button>
    </div>
  `);
}

function exportSpecs() {
  const state = window.builderState;
  if (!state.model) return;
  
  let text = `Norlyth Custom Build\n`;
  text += `Model: ${state.model.title}\n`;
  text += `------------------------\n`;
  
  Object.entries(state.selectedOptions).forEach(([prop, opt]) => {
    text += `${prop}: ${opt.title}\n`;
  });
  
  text += `------------------------\n`;
  text += `Total Price: $${calculateTotalPrice().toLocaleString()}\n`;
  
  const currentModelTitle = state.model ? state.model.title : 'Custom Guitar';
  const filename = `Norlyth-${currentModelTitle.replace(/\s+/g, '-')}-Specs.txt`;
  
  const blob = new Blob([text], { type: 'text/plain' });
  const textUrl = URL.createObjectURL(blob);
  
  showExportModal('Export Specs', `
    <textarea id="export-specs-textarea" readonly style="width: 100%; height: 250px; background: #1a1a1a; color: #eee; border: 1px solid #333; padding: 10px; border-radius: 4px; font-family: monospace; font-size: 13px; resize: none; margin-bottom: 20px;">${text}</textarea>
    <div style="display: flex; gap: 10px;">
      <button class="btn-add-to-cart" style="flex: 1; padding: 10px; font-size: 14px; margin-top: 0; background: transparent; border: 1px solid var(--COLOR-PRIMARY); color: var(--COLOR-PRIMARY);" onclick="copyToClipboard('export-specs-textarea', this)">Copy to Clipboard</button>
      <a href="${textUrl}" download="${filename}" style="flex: 1; padding: 10px; font-size: 14px; margin-top: 0; background: var(--COLOR-PRIMARY); color: #000; text-align: center; text-decoration: none; border-radius: 4px; font-weight: bold; display: flex; align-items: center; justify-content: center;">Download .txt</a>
    </div>
  `);
}

window.exportShareBuild = exportShareBuild;
window.exportSpecs = exportSpecs;
window.exportImage = exportImage;
window.closeExportModal = closeExportModal;

function exportImage() {
  const visContainer = document.querySelector('.visualizer-image-container');
  if (!visContainer) return;
  
  showToast('Rendering image... Please wait.', 5000);
  
  // Dynamically load html2canvas if not present
  if (typeof html2canvas === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    script.onload = () => doExportImage(visContainer);
    document.head.appendChild(script);
  } else {
    doExportImage(visContainer);
  }
}

async function doExportImage(visContainer) {
  const state = window.builderState;
  const currentView = state.view;
  const currentModelTitle = state.model ? state.model.title : 'Custom Guitar';
  
  // Setup composite container hidden offscreen
  const composite = document.createElement('div');
  composite.style.position = 'fixed';
  composite.style.left = '-9999px';
  composite.style.top = '0';
  composite.style.width = '1600px';
  composite.style.height = '1000px';
  const bgCol = state.bgColor || '#111';
  composite.style.backgroundColor = bgCol;
  composite.style.display = 'flex';
  composite.style.flexDirection = 'row';
  composite.style.alignItems = 'center';
  composite.style.justifyContent = 'space-around';
  composite.style.padding = '50px';
  
  const logo = document.createElement('div');
  logo.style.position = 'absolute';
  logo.style.top = '40px';
  logo.style.left = '40px';
  logo.style.color = (bgCol === '#fff') ? '#000' : '#fff';
  logo.style.fontSize = '32px';
  logo.style.fontWeight = 'bold';
  logo.innerHTML = 'NORLYTH <span style="color: var(--COLOR-PRIMARY);">CUSTOM</span>';
  composite.appendChild(logo);
  
  const titleLabel = document.createElement('div');
  titleLabel.style.position = 'absolute';
  titleLabel.style.top = '40px';
  titleLabel.style.right = '40px';
  titleLabel.style.color = (bgCol === '#fff') ? '#333' : '#ccc';
  titleLabel.style.fontSize = '24px';
  titleLabel.style.textAlign = 'right';
  titleLabel.innerHTML = `<div style="font-size: 14px; color: ${(bgCol === '#fff') ? '#666' : '#888'};">MODEL</div>${currentModelTitle}`;
  composite.appendChild(titleLabel);
  
  const priceLabel = document.createElement('div');
  priceLabel.style.position = 'absolute';
  priceLabel.style.bottom = '40px';
  priceLabel.style.right = '40px';
  priceLabel.style.color = (bgCol === '#fff') ? '#000' : '#fff';
  priceLabel.style.fontSize = '32px';
  priceLabel.style.textAlign = 'right';
  priceLabel.innerHTML = `<div style="font-size: 14px; color: ${(bgCol === '#fff') ? '#666' : '#888'};">TOTAL</div>$${calculateTotalPrice().toLocaleString()}`;
  composite.appendChild(priceLabel);

  const frontWrapper = document.createElement('div');
  frontWrapper.style.width = '700px';
  frontWrapper.style.height = '800px';
  frontWrapper.style.position = 'relative';
  if (state.dexterity === 'left') {
    frontWrapper.style.transform = 'scaleX(-1)';
  }
  
  const rearWrapper = document.createElement('div');
  rearWrapper.style.width = '700px';
  rearWrapper.style.height = '800px';
  rearWrapper.style.position = 'relative';
  if (state.dexterity === 'left') {
    rearWrapper.style.transform = 'scaleX(-1)';
  }

  composite.appendChild(frontWrapper);
  composite.appendChild(rearWrapper);
  document.body.appendChild(composite);
  
  window.printDecalsToDownload = {};

  const buildExportWrapper = async (targetWrapper, viewName) => {
    const currentVisContainer = document.querySelector('.visualizer-image-container');
    const visW = currentVisContainer ? currentVisContainer.clientWidth : 500;
    const visH = currentVisContainer ? currentVisContainer.clientHeight : 700;
    
    const layers = getLayersForView(viewName);
    const decalState = state.customDecals[viewName];
    
    // 1. Wait for all layer images to load completely
    await Promise.all(layers.map(layer => {
      return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = resolve;
        img.onerror = resolve; // Continue on error to prevent hanging
        img.src = layer.url;
      });
    }));
    
    // 2. Append them to the wrapper
    layers.forEach(layer => {
      const img = document.createElement('img');
      img.src = layer.url;
      img.style.height = '100%';
      img.style.width = 'auto';
      img.style.position = 'absolute';
      img.style.left = '50%';
      img.style.transform = 'translateX(-50%)';
      img.style.zIndex = layer.zindex;
      targetWrapper.appendChild(img);
    });
    
    // 3. Process Custom Decal if exists
    if (decalState) {
       const maskLayer = layers.find(l => l.id.toLowerCase().includes('body')) 
                      || layers.find(l => l.zindex > 0 && l.zindex < 20) 
                      || layers.find(l => l.id.toLowerCase().includes('wood')) 
                      || layers.find(l => l.id === 'model');
       if (maskLayer) {
         const canvas = document.createElement('canvas');
         canvas.style.height = '100%';
         canvas.style.width = 'auto';
         canvas.style.position = 'absolute';
         canvas.style.left = '50%';
         canvas.style.transform = 'translateX(-50%)';
         canvas.style.zIndex = maskLayer.zindex + 1;
         const ctx = canvas.getContext('2d');
         
         await new Promise(resolve => {
           const mImg = new Image();
           mImg.crossOrigin = 'Anonymous';
           mImg.onload = () => {
             canvas.width = mImg.naturalWidth;
             canvas.height = mImg.naturalHeight;
             ctx.drawImage(mImg, 0, 0, canvas.width, canvas.height);
             
             const printCanvas = document.createElement('canvas');
             printCanvas.width = canvas.width; 
             printCanvas.height = canvas.height;
             const pCtx = printCanvas.getContext('2d');
             pCtx.drawImage(mImg, 0, 0, printCanvas.width, printCanvas.height);
             pCtx.globalCompositeOperation = 'source-in';
             
             const dImg = new Image();
             dImg.crossOrigin = 'Anonymous';
             dImg.onload = () => {
               // Calculate the on-screen rendered size of the 90% contain mask
               const maxW = visW * 0.9;
               const maxH = visH * 0.9;
               const screenScale = Math.min(maxW / canvas.width, maxH / canvas.height);
               const screenRenderedW = canvas.width * screenScale;
               
               // Mathematical scale factor mapping on-screen container to full-resolution canvas
               const scaleFactor = canvas.width / screenRenderedW;
               
               ctx.globalCompositeOperation = 'source-atop';
               
               [ctx, pCtx].forEach(c => {
                 c.save();
                 
                 const centerX = canvas.width / 2;
                 const centerY = canvas.height / 2;
                 
                 c.translate(centerX + decalState.x * scaleFactor, centerY + decalState.y * scaleFactor);
                 c.scale(decalState.scale, decalState.scale);
                 c.rotate(decalState.rotation * Math.PI / 180);
                 
                 // Render at fixed 300px base size scaled up to canvas size
                 const decalW = decalState.width || 300;
                 const decalH = decalState.height || 300;
                 const scaledW = decalW * scaleFactor;
                 const scaledH = decalH * scaleFactor;
                 c.drawImage(dImg, -scaledW/2, -scaledH/2, scaledW, scaledH);
                 c.restore();
               });
               
               window.printDecalsToDownload[viewName] = printCanvas.toDataURL('image/png');
               resolve();
             };
             dImg.src = decalState.dataUrl;
           };
           mImg.src = maskLayer.url;
         });
         targetWrapper.appendChild(canvas);
       }
    }
  };

  await buildExportWrapper(frontWrapper, 'front');
  await buildExportWrapper(rearWrapper, 'rear');

  // Wait a small moment for browser to render the DOM changes
  await new Promise(r => setTimeout(r, 100));

  try {
    const canvas = await html2canvas(composite, { backgroundColor: bgCol, useCORS: true });
    const imgDataUrl = canvas.toDataURL('image/png');
    const filename = `Norlyth-${currentModelTitle.replace(/\s+/g, '-')}-Build.png`;
    document.body.removeChild(composite);
    
    showExportModal('Export Image', `
      <div style="text-align: center; margin-bottom: 20px;">
        <img src="${imgDataUrl}" style="max-width: 100%; height: auto; border: 1px solid #333; border-radius: 4px;" alt="Custom Build" />
      </div>
      <div style="display: flex; gap: 10px; flex-direction: column;">
        <a href="${imgDataUrl}" download="${filename}" style="padding: 12px; font-size: 16px; background: var(--COLOR-PRIMARY); color: #000; text-align: center; text-decoration: none; border-radius: 4px; font-weight: bold; width: 100%;">Download Effect Image</a>
        ${window.printDecalsToDownload['front'] ? `<a href="${window.printDecalsToDownload['front']}" download="Norlyth-Print-File-Front.png" style="padding: 10px; font-size: 14px; background: #333; border: 1px solid #555; color: #fff; text-align: center; text-decoration: none; border-radius: 4px; width: 100%;">Download Front Print File</a>` : ''}
        ${window.printDecalsToDownload['back'] ? `<a href="${window.printDecalsToDownload['back']}" download="Norlyth-Print-File-Back.png" style="padding: 10px; font-size: 14px; background: #333; border: 1px solid #555; color: #fff; text-align: center; text-decoration: none; border-radius: 4px; width: 100%;">Download Back Print File</a>` : ''}
      </div>
    `);
  } catch(err) {
    console.error(err);
    document.body.removeChild(composite);
    showToast('Error rendering image. Please try again.');
  }
}

function copyToClipboard(inputId, btn) {
  const input = document.getElementById(inputId);
  if (!input) return;
  input.select();
  document.execCommand('copy');
  const oldText = btn.innerText;
  btn.innerText = 'Copied!';
  setTimeout(() => btn.innerText = oldText, 2000);
}

function showToast(message, duration = 3000) {
  let toast = document.getElementById('builder-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'builder-toast';
    document.body.appendChild(toast);
  }
  toast.innerText = message;
  toast.classList.add('show');
  setTimeout(() => {
    toast.classList.remove('show');
  }, duration);
}

window.copyToClipboard = copyToClipboard;

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
}
window.downloadTextFile = downloadTextFile;

function showExportModal(title, contentHTML) {
  let modal = document.getElementById('export-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'export-modal';
    modal.innerHTML = `
      <div class="export-modal-overlay" onclick="closeExportModal()"></div>
      <div class="export-modal-content">
        <h3 id="export-modal-title" style="margin-top: 0; color: #fff; font-size: 20px;"></h3>
        <div id="export-modal-body" style="margin-top: 20px;"></div>
        <button class="btn-close-menu" style="margin-top: 30px; width: 100%; border: 1px solid #444; background: transparent; color: #fff; padding: 12px; border-radius: 4px; cursor: pointer; font-size: 14px;" onclick="closeExportModal()">Close</button>
      </div>
    `;
    document.body.appendChild(modal);
  }
  document.getElementById('export-modal-title').innerText = title;
  document.getElementById('export-modal-body').innerHTML = contentHTML;
  modal.classList.add('show');
}

function closeExportModal() {
  const modal = document.getElementById('export-modal');
  if (modal) modal.classList.remove('show');
}

// ==========================================
// CUSTOM FINISH (DECAL) LOGIC
// ==========================================

function renderCustomFinishUI() {
  const state = window.builderState;
  const view = state.view; 
  const decal = state.customDecals[view];
  
  let feeMsg = '';
  if (window.NORLYTH_BUILDER_CONFIG && window.NORLYTH_BUILDER_CONFIG.customFinishFeePrice) {
    const feeAmt = parseFloat(window.NORLYTH_BUILDER_CONFIG.customFinishFeePrice) / 100;
    if (feeAmt > 0) {
      feeMsg = `<div style="margin-top: 25px; padding: 15px; background: rgba(255,255,255,0.05); border-radius: 8px; font-size: 13px; color: #ccc; border: 1px solid rgba(255,255,255,0.1);">
                  <strong style="color: #fff; display: block; margin-bottom: 5px;">Customization Fee:</strong> 
                  A flat rate of <strong style="color: #dcb378;">$${feeAmt.toFixed(2)}</strong> will be added to your total. This single fee covers graphics for both the front and rear of the guitar.
                </div>`;
    }
  }
  
  return `
    <div class="options-header">
      <h3>Custom Finish (${view === 'front' ? 'Front' : 'Back'})</h3>
      <button class="btn-close-options" onclick="closeMobileOptions()">&times;</button>
    </div>
    <div class="options-grid-container" style="padding: 20px;">
      <p style="margin-bottom: 15px; color: #aaa; font-size: 14px;">
        Upload a transparent PNG to apply a custom graphic to the ${view} of the guitar body. You can drag the graphic directly on the guitar to position it.
      </p>
      
      <div style="margin-bottom: 20px;">
        <label for="decal-upload-${view}" class="btn btn--primary" style="display:inline-block; cursor:pointer; width:100%; text-align:center;">
          Upload ${view === 'front' ? 'Front' : 'Back'} Graphic
        </label>
        <input type="file" id="decal-upload-${view}" accept="image/png, image/jpeg" style="display:none;" onchange="handleDecalUpload(event, '${view}')">
      </div>

      ${decal ? `
        <button class="btn" style="width:100%; text-align:center; background: #333; color:#fff; border: 1px solid rgba(255,255,255,0.2);" onclick="removeDecal('${view}')">
          Remove Graphic
        </button>
      ` : ''}
      
      ${feeMsg}
    </div>
  `;
}

window.handleDecalUpload = function(e, view) {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const initScale = Math.min(300 / img.naturalWidth, 300 / img.naturalHeight, 1);
      window.builderState.customDecals[view] = {
        dataUrl: ev.target.result,
        x: 0,
        y: 0,
        scale: initScale,
        rotation: 0,
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      
      // General Mutually Exclusive Engine (Custom Finish context)
      if (typeof MUTUALLY_EXCLUSIVE_RULES !== 'undefined' && MUTUALLY_EXCLUSIVE_RULES['Custom Finish']) {
         MUTUALLY_EXCLUSIVE_RULES['Custom Finish'].forEach(conflictProp => {
            delete window.builderState.selectedOptions[conflictProp];
         });
      }
      
      updateVisualizer();
      updateOptionsGrid();
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
};

window.updateDecal = function(prop, val) {
  const view = window.builderState.view;
  if(window.builderState.customDecals[view]) {
    window.builderState.customDecals[view][prop] = parseFloat(val);
    updateVisualizer();
    updateOptionsGrid();
  }
};

window.removeDecal = function(view) {
  window.builderState.customDecals[view] = null;
  updateVisualizer();
  updateOptionsGrid();
};

window.initDecalDrag = function() {
  const overlay = document.querySelector('.decal-controls-overlay');
  if (!overlay) return;
  
  if (window._decalDragListeners) {
    window.removeEventListener('mousemove', window._decalDragListeners.onMove);
    window.removeEventListener('mouseup', window._decalDragListeners.onEnd);
    window.removeEventListener('touchmove', window._decalDragListeners.onMove);
    window.removeEventListener('touchend', window._decalDragListeners.onEnd);
    window._decalDragListeners = null;
  }

  const bbox = overlay.querySelector('.decal-bounding-box');
  const handleScale = overlay.querySelector('.decal-handle-scale');
  const handleRotate = overlay.querySelector('.decal-handle-rotate');
  
  if (!bbox) return;

  let currentAction = null; // 'drag', 'scale', 'rotate'
  let startClientX, startClientY;
  let initialX, initialY, initialScale, initialRotation;
  let centerX, centerY;
  let initialDist, initialAngle;
  
  const state = window.builderState;

  const getClientPos = (e) => {
    return {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
  };

  const getCenter = () => {
    const rect = bbox.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  const startInteraction = (e, action) => {
    e.stopPropagation(); // prevent drag if clicking handle
    currentAction = action;
    const pos = getClientPos(e);
    startClientX = pos.x;
    startClientY = pos.y;
    
    const view = state.view;
    const d = state.customDecals[view];
    initialX = d.x;
    initialY = d.y;
    initialScale = d.scale;
    initialRotation = d.rotation;
    
    const center = getCenter();
    centerX = center.x;
    centerY = center.y;
    
    if (action === 'scale') {
      initialDist = Math.hypot(startClientX - centerX, startClientY - centerY);
    } else if (action === 'rotate') {
      let dx = startClientX - centerX;
      if (state.dexterity === 'left') dx = -dx;
      initialAngle = Math.atan2(startClientY - centerY, dx) * 180 / Math.PI;
    }
  };

  const onDragStart = (e) => startInteraction(e, 'drag');
  const onScaleStart = (e) => startInteraction(e, 'scale');
  const onRotateStart = (e) => startInteraction(e, 'rotate');

  const onMove = (e) => {
    if (!currentAction) return;
    e.preventDefault();
    const pos = getClientPos(e);
    const view = state.view;
    const d = state.customDecals[view];
    
    if (currentAction === 'drag') {
      let dx = pos.x - startClientX;
      if (state.dexterity === 'left') dx = -dx;
      d.x = initialX + dx;
      d.y = initialY + (pos.y - startClientY);
    } else if (currentAction === 'scale') {
      const currentDist = Math.hypot(pos.x - centerX, pos.y - centerY);
      // Optional: limit min scale
      const newScale = initialScale * (currentDist / initialDist);
      d.scale = Math.max(0.05, newScale);
    } else if (currentAction === 'rotate') {
      let dx = pos.x - centerX;
      if (state.dexterity === 'left') dx = -dx;
      const currentAngle = Math.atan2(pos.y - centerY, dx) * 180 / Math.PI;
      const angleDiff = currentAngle - initialAngle;
      d.rotation = initialRotation + angleDiff;
    }
    
    updateOptionsGrid(); // Sync sliders if panel is open
    // Fast inline update without full re-render
    const img = document.querySelector('.decal-image');
    if (img && bbox) {
      const transformStr = `translate(${d.x}px, ${d.y}px) scale(${d.scale}) rotate(${d.rotation}deg)`;
      img.style.transform = transformStr;
      
      const invScale = 1 / d.scale;
      const borderThickness = Math.max(1, 2 * invScale);
      bbox.style.transform = transformStr;
      bbox.style.borderWidth = `${borderThickness}px`;
      bbox.style.boxShadow = `0 0 0 ${1*invScale}px rgba(0,0,0,0.3)`;
      
      const hScale = document.querySelector('.decal-handle-scale');
      const hRotate = document.querySelector('.decal-handle-rotate');
      const handleSize = 28 * invScale;
      const handleOffset = -35 * invScale;
      const handleOffsetScale = -14 * invScale;
      
      if (hScale) {
        hScale.style.width = `${handleSize}px`;
        hScale.style.height = `${handleSize}px`;
        hScale.style.bottom = `${handleOffsetScale}px`;
        hScale.style.right = `${handleOffsetScale}px`;
        hScale.style.borderWidth = `${1*invScale}px`;
        hScale.style.boxShadow = `0 ${2*invScale}px ${4*invScale}px rgba(0,0,0,0.2)`;
      }
      if (hRotate) {
        hRotate.style.width = `${handleSize}px`;
        hRotate.style.height = `${handleSize}px`;
        hRotate.style.bottom = `${handleOffset}px`;
        hRotate.style.borderWidth = `${1*invScale}px`;
        hRotate.style.boxShadow = `0 ${2*invScale}px ${4*invScale}px rgba(0,0,0,0.2)`;
      }
    }
  };

  const onEnd = () => {
    currentAction = null;
  };

  // Bind Mouse
  bbox.addEventListener('mousedown', onDragStart);
  if (handleScale) handleScale.addEventListener('mousedown', onScaleStart);
  if (handleRotate) handleRotate.addEventListener('mousedown', onRotateStart);
  window.addEventListener('mousemove', onMove, { passive: false });
  window.addEventListener('mouseup', onEnd);
  
  // Bind Touch
  bbox.addEventListener('touchstart', onDragStart, { passive: true });
  if (handleScale) handleScale.addEventListener('touchstart', onScaleStart, { passive: true });
  if (handleRotate) handleRotate.addEventListener('touchstart', onRotateStart, { passive: true });
  window.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('touchend', onEnd);
  
  window._decalDragListeners = { onMove, onEnd };
};
